import { MS_DOCUMENT_TYPES, MS_MARKETS } from "@repo/shared";
import type { LocalizedString, ProductDocumentType } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useState, useTransition } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { LocalizedInput } from "@/components/ui/localized-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDocumentType, useUpdateDocumentType } from "@/lib/hooks/use-product-documents";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./product-document-type-dialog.i18n";

type ProductDocumentTypeDialogProps = {
  open: boolean;
  portfolioCollectionId: string;
  documentType: ProductDocumentType | null;
  onClose: () => void;
};

export function ProductDocumentTypeDialog({
  open,
  portfolioCollectionId,
  documentType,
  onClose,
}: ProductDocumentTypeDialogProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);

  const editingDocumentTypeName = documentType ? localize(documentType.nameI18n) : "";

  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [msOpen, setMsOpen] = useState(false);
  const createDocumentTypeMutation = useCreateDocumentType();
  const updateDocumentTypeMutation = useUpdateDocumentType();

  const [name, setName] = useState("");
  const [nameI18n, setNameI18n] = useState<LocalizedString>({});
  const [formData, setFormData] = useState({
    msDocumentTypeId: "",
    msMarket: "",
  });

  useEffect(() => {
    if (open) {
      if (documentType) {
        // Extract user's language value from nameI18n
        const i18n = documentType.nameI18n || {};
        const nameInUserLang = localize(i18n);
        // Store other translations separately
        const otherTranslations = Object.fromEntries(
          Object.entries(i18n).filter(([lang]) => lang !== userLanguage),
        );
        setName(nameInUserLang);
        setNameI18n(otherTranslations);
        setFormData({
          msDocumentTypeId: documentType.msDocumentTypeId || "",
          msMarket: documentType.msMarket || "",
        });
        // Auto-expand MS section if any values are set
        setMsOpen(!!(documentType.msDocumentTypeId || documentType.msMarket));
      } else {
        setName("");
        setNameI18n({});
        setFormData({
          msDocumentTypeId: "",
          msMarket: "",
        });
        setMsOpen(false);
      }
      setError(null);
    }
  }, [documentType, open, userLanguage, localize]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t.nameRequired);
      return;
    }

    // Build full i18n object with user's language value
    const fullNameI18n: LocalizedString = {
      ...nameI18n,
      [userLanguage]: name.trim(),
    };

    startTransition(async () => {
      const submitData = {
        nameI18n: fullNameI18n,
        msDocumentTypeId: formData.msDocumentTypeId || undefined,
        msMarket: formData.msMarket || undefined,
      };

      try {
        if (documentType) {
          await updateDocumentTypeMutation.mutateAsync({
            collectionId: portfolioCollectionId,
            typeId: documentType.id,
            data: submitData,
          });
        } else {
          await createDocumentTypeMutation.mutateAsync({
            collectionId: portfolioCollectionId,
            data: submitData,
          });
        }
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : t.failedToSave);
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {documentType
              ? editingDocumentTypeName
                ? `${t.editDocumentType} (${editingDocumentTypeName})`
                : t.editDocumentType
              : t.addDocumentType}
          </DialogTitle>
          <DialogDescription>
            {documentType ? t.editDescription : t.createDescription}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <LocalizedInput
                id="name"
                label={t.name}
                value={name}
                i18nValue={nameI18n}
                onChange={setName}
                onI18nChange={setNameI18n}
                supportedLanguages={tenantSupportedLanguages}
                placeholder={t.namePlaceholder}
                disabled={isPending}
                required
              />
            </div>

            <div>
              <Button
                type="button"
                variant="ghost"
                className="w-full justify-between px-0 hover:bg-transparent"
                onClick={() => setMsOpen(!msOpen)}
              >
                <span className="text-sm font-medium">{t.morningstarMapping}</span>
                {msOpen ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
              {msOpen && (
                <div className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">{t.morningstarMappingDesc}</p>

                  <div className="space-y-2">
                    <Label htmlFor="msDocumentTypeId">{t.documentType}</Label>
                    <Select
                      value={formData.msDocumentTypeId}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          msDocumentTypeId: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.selectDocumentType} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.none}</SelectItem>
                        {MS_DOCUMENT_TYPES.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="msMarket">{t.market}</Label>
                    <Select
                      value={formData.msMarket}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          msMarket: value === "__none__" ? "" : value,
                        })
                      }
                      disabled={isPending}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t.anyMarket} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t.anyMarket}</SelectItem>
                        {MS_MARKETS.map((market) => (
                          <SelectItem key={market.id} value={market.id}>
                            {market.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.marketHelpText}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogCancel disabled={isPending}>{t.cancel}</DialogCancel>
            <Button type="submit" loading={isPending}>
              {documentType ? t.save : t.addDocumentType}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
